import { useEffect } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { ErrorType } from '@/@types/errors'
import { toast } from 'sonner'

export const useOfflineSync = () => {
  const MAX_RETRIES = 3
  const {
    setOffline,
    setSyncing,
    removeFromQueue,
    updateQueueItem,
    setGlobalError,
    accessToken,
  } = useAppStore()
  
  useEffect(() => {
    setOffline(!navigator.onLine)

    const handleOnline = async () => {
      setOffline(false)

      const queue = useAppStore
        .getState()
        .offlineQueue
        .filter((item) => item.retries < MAX_RETRIES)
      if (queue.length === 0) return

      setSyncing(true)

      try {
        const result = await fetch('/api/sync/batch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ items: queue }),
        })

        if (!result.ok) {
          throw new Error('Failed to sync offline queue')
        }

        const { successful = [], failed = [] } = await result.json()
        successful.forEach((id: string) => removeFromQueue(id))

        failed.forEach((failedItem: { id: string; reason: string }) => {
          const currentItem = useAppStore.getState().offlineQueue.find((item) => item.id === failedItem.id)
          if (!currentItem) return

          updateQueueItem(failedItem.id, { retries: currentItem.retries + 1 })
        })

        if (failed.length > 0) {
          setGlobalError({
            message: `${failed.length} queued item(s) failed to sync and will be retried.`,
            type: ErrorType.NETWORK_ERROR,
          })
          toast.error(`${failed.length} queued item(s) failed to sync`)
        } else if (successful.length > 0) {
          toast.success(`Synced ${successful.length} offline item(s)`)
        }
      } catch (error) {
        console.error('Sync failed:', error)
        setGlobalError({
          message: 'Offline sync failed. Pending items will retry when online.',
          type: ErrorType.NETWORK_ERROR,
        })
      } finally {
        setSyncing(false)
      }
    }
    
    const handleOffline = () => {
      setOffline(true)
    }
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    if (navigator.onLine) {
      handleOnline()
    }
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [accessToken, removeFromQueue, setGlobalError, setOffline, setSyncing, updateQueueItem])
}
